import React, { useRef, useState, useEffect } from "react";
import { Link, useLocation, useSearchParams } from "react-router-dom";
import Select from "react-select";
import axios from "axios";

const formatNumberCH = (value, options = {}) => {
    if (value === null || value === undefined || value === "") {
        return "0";
    }
    const numberValue = typeof value === "number" ? value : Number(value);
    if (!Number.isFinite(numberValue)) {
        return value?.toString ? value.toString() : "";
    }
    const {
        minimumFractionDigits = 0,
        maximumFractionDigits = Math.max(minimumFractionDigits, 2),
    } = options;

    return numberValue.toLocaleString("de-CH", {
        minimumFractionDigits,
        maximumFractionDigits,
    });
};

const Keys = () => {
    const allBuildingsOption = { value: "All", label: "All Buildings" };
    const allPeriodsOption = { value: "All", label: "All Time Periods" };
    const location = useLocation();
    const [searchParams, setSearchParams] = useSearchParams();
    const buildingParam = searchParams.get("building") || "All";
    const periodParam = searchParams.get("period") || "All";
    const searchParamFromUrl = searchParams.get("search") || "";
    const pageParam = Number(searchParams.get("page"));
    const initialPage = Number.isFinite(pageParam) && pageParam > 0 ? pageParam : 1;
    const [keys, setKeys] = useState([]);
    const [loading, setLoading] = useState(true);
    const [buildings, setBuildings] = useState([]);
    const [selectedBuilding, setSelectedBuilding] = useState(
        buildingParam === "All"
            ? allBuildingsOption
            : { value: buildingParam, label: "Selected Building" }
    );
    const [periods, setPeriods] = useState([allPeriodsOption]);
    const [selectedPeriod, setSelectedPeriod] = useState(
        periodParam === "All"
            ? allPeriodsOption
            : { value: periodParam, label: "Selected Time Period" }
    );
    const [searchTerm, setSearchTerm] = useState(searchParamFromUrl);
    const [page, setPage] = useState(initialPage);
    // Client feedback 2026-07-21: bump default page size from 50 to 100 for the
    // Keys listing so large projects (Étang A has 55+ keys, Sévelin etc.) fit on
    // one page instead of two.
    const [pageSize] = useState(100);
    const [pagination, setPagination] = useState({ total: 0, totalPages: 0 });
    const [orderDrafts, setOrderDrafts] = useState({});
    const [orderSavingKeyId, setOrderSavingKeyId] = useState(null);
    const [isSavingAllOrders, setIsSavingAllOrders] = useState(false);
    
    // Calculation modal state
    const [showCalculations, setShowCalculations] = useState(false);
    const [selectedKeyCalculations, setSelectedKeyCalculations] = useState([]);
    const [recalculatedTransfers, setRecalculatedTransfers] = useState([]);
    const [calculationsLoading, setCalculationsLoading] = useState(false);
    const [selectedKeyName, setSelectedKeyName] = useState("");

    // Result II modal state
    const [showResultII, setShowResultII] = useState(false);
    const [selectedKeyResultII, setSelectedKeyResultII] = useState([]);
    const [resultIILoading, setResultIILoading] = useState(false);
    const [selectedResultIIKeyName, setSelectedResultIIKeyName] = useState("");
    const returnTo = encodeURIComponent(`${location.pathname}${location.search}`);
    const selectedBuildingValue = selectedBuilding?.value === undefined || selectedBuilding?.value === null
        ? "All"
        : String(selectedBuilding.value);
    const selectedPeriodValue = selectedPeriod?.value === undefined || selectedPeriod?.value === null
        ? "All"
        : String(selectedPeriod.value);
    const hasSkippedInitialFilterEffect = useRef(false);

    useEffect(() => {
        fetchBuildings();
    }, []);

    useEffect(() => {
        const nextParams = new URLSearchParams();
        if (selectedBuildingValue !== "All") {
            nextParams.set("building", selectedBuildingValue);
        }
        if (selectedPeriodValue !== "All") {
            nextParams.set("period", selectedPeriodValue);
        }
        if (searchTerm?.trim()) {
            nextParams.set("search", searchTerm.trim());
        }
        if (page > 1) {
            nextParams.set("page", String(page));
        }
        const currentQuery = searchParams.toString();
        const nextQuery = nextParams.toString();
        if (currentQuery !== nextQuery) {
            setSearchParams(nextParams, { replace: true });
        }
    }, [selectedBuildingValue, selectedPeriodValue, searchTerm, page, searchParams, setSearchParams]);

    useEffect(() => {
        // Reset to first page when filters change (unless already on page 1)
        if (!hasSkippedInitialFilterEffect.current) {
            hasSkippedInitialFilterEffect.current = true;
            return;
        }

        if (page !== 1) {
            setPage(1);
        } else {
            // Only fetch if already on page 1 to avoid double fetch
            fetchKeys();
        }
    }, [searchTerm, selectedBuildingValue, selectedPeriodValue]);

    useEffect(() => {
        // Fetch when page changes (this handles page navigation)
        fetchKeys();
    }, [page]);

    const fetchBuildings = async () => {
        try {
            const res = await axios.get(`${process.env.REACT_APP_API_URL}key/buildings`, {
                headers: {
                    "api-key": process.env.REACT_APP_API_KEY,
                    Authorization: `Bearer ${localStorage.getItem("token")}`,
                },
            });
            if (res.data.status === "success") {
                const buildingOptions = [
                    allBuildingsOption,
                    ...res.data.data.buildings.map(building => ({
                        value: building.id,
                        label: building.building_name
                    }))
                ];
                setBuildings(buildingOptions);

                // Rehydrate previously selected building/period on page revisit.
                if (selectedBuilding?.value && selectedBuilding.value !== "All") {
                    const restoredBuilding = buildingOptions.find(
                        (option) => String(option.value) === String(selectedBuilding.value)
                    );
                    if (restoredBuilding) {
                        setSelectedBuilding(restoredBuilding);
                        await fetchPeriodsForBuilding(restoredBuilding.value, {
                            preserveSelectedPeriodValue: selectedPeriod?.value
                        });
                    } else {
                        setSelectedBuilding(allBuildingsOption);
                        setSelectedPeriod(allPeriodsOption);
                    }
                }
            }
        } catch (err) {
            console.error("Error fetching buildings:", err);
        }
    };

    const fetchPeriodsForBuilding = async (buildingId, options = {}) => {
        const { preserveSelectedPeriodValue } = options;
        if (!buildingId || buildingId === "All") {
            setPeriods([allPeriodsOption]);
            setSelectedPeriod(allPeriodsOption);
            return;
        }

        try {
            const res = await axios.get(`${process.env.REACT_APP_API_URL}building/${buildingId}/periods`, {
                headers: {
                    "api-key": process.env.REACT_APP_API_KEY,
                    Authorization: `Bearer ${localStorage.getItem("token")}`,
                },
            });

            if (res.data.status === "success") {
                const periodOptions = [
                    allPeriodsOption,
                    ...(res.data.data.periods || []).map((period) => ({
                        value: period.id,
                        label: `${new Date(period.start_date).toLocaleDateString()} - ${new Date(period.end_date).toLocaleDateString()}`
                    }))
                ];
                setPeriods(periodOptions);

                if (preserveSelectedPeriodValue && preserveSelectedPeriodValue !== "All") {
                    const restoredPeriod = periodOptions.find(
                        (period) => String(period.value) === String(preserveSelectedPeriodValue)
                    );
                    setSelectedPeriod(restoredPeriod || allPeriodsOption);
                } else {
                    setSelectedPeriod(allPeriodsOption);
                }
            } else {
                setPeriods([allPeriodsOption]);
                setSelectedPeriod(allPeriodsOption);
            }
        } catch (err) {
            console.error("Error fetching periods:", err);
            setPeriods([allPeriodsOption]);
            setSelectedPeriod(allPeriodsOption);
        }
    };

    const handleBuildingChange = async (selected) => {
        const nextBuilding = selected || allBuildingsOption;
        setSelectedBuilding(nextBuilding);
        await fetchPeriodsForBuilding(nextBuilding.value);
    };

    const fetchKeys = async () => {
        try {
            setLoading(true);
            const buildingId = selectedBuilding && selectedBuilding.value && selectedBuilding.value !== "All" 
                ? selectedBuilding.value 
                : undefined;
            const periodId = selectedPeriod && selectedPeriod.value && selectedPeriod.value !== "All"
                ? selectedPeriod.value
                : undefined;
            const searchParam = searchTerm && searchTerm.trim() ? searchTerm.trim() : undefined;
            
            const res = await axios.get(`${process.env.REACT_APP_API_URL}key/list`, {
                headers: {
                    "api-key": process.env.REACT_APP_API_KEY,
                    Authorization: `Bearer ${localStorage.getItem("token")}`,
                },
                params: {
                    page,
                    pageSize,
                    building_id: buildingId,
                    building_time_period_id: periodId,
                    search: searchParam
                }
            });
            
            if (res.data.status === "success") {
                const keysData = res.data.data.keys || [];
                console.log("Keys data received:", keysData);
                if (keysData.length > 0) {
                    console.log("Sample key costs:", keysData[0].costs);
                }
                setKeys(keysData);
                setPagination(res.data.data.pagination || { total: 0, totalPages: 0 });
            } else {
                setKeys([]);
                setPagination({ total: 0, totalPages: 0 });
            }
        } catch (err) {
            console.error("Error fetching keys:", err);
            setKeys([]);
            setPagination({ total: 0, totalPages: 0 });
        } finally {
            setLoading(false);
        }
    };

    const handleSaveKeyOrder = async (keyId) => {
        const raw = orderDrafts[keyId];
        const normalized =
            raw === "" || raw === null || raw === undefined ? null : parseInt(raw, 10);

        if (normalized !== null && (!Number.isInteger(normalized) || normalized < 1)) {
            alert("Key order must be a positive integer (or empty to clear).");
            return;
        }

        try {
            setOrderSavingKeyId(keyId);
            await axios.patch(`${process.env.REACT_APP_API_URL}key/${keyId}/order`, { key_order: normalized }, {
                headers: {
                    "api-key": process.env.REACT_APP_API_KEY,
                    Authorization: `Bearer ${localStorage.getItem("token")}`,
                    "Content-Type": "application/json"
                }
            });
            setOrderDrafts((prev) => {
                const next = { ...prev };
                delete next[keyId];
                return next;
            });
            fetchKeys();
        } catch (err) {
            console.error("Error saving key order:", err);
            alert("Error saving order: " + (err.response?.data?.message || err.message || "Unknown error"));
        } finally {
            setOrderSavingKeyId(null);
        }
    };


    const handleSaveAllOrders = async () => {
        const dirtyIds = Object.keys(orderDrafts);
        if (dirtyIds.length === 0) return;

        const invalid = dirtyIds.find((id) => {
            const raw = orderDrafts[id];
            const val = raw === "" || raw === null || raw === undefined ? null : parseInt(raw, 10);
            return val !== null && (!Number.isInteger(val) || val < 1);
        });
        if (invalid) {
            alert("Key order must be a positive integer (or empty to clear).");
            return;
        }

        setIsSavingAllOrders(true);
        try {
            await Promise.all(
                dirtyIds.map((id) => {
                    const raw = orderDrafts[id];
                    const normalized = raw === "" || raw === null || raw === undefined ? null : parseInt(raw, 10);
                    return axios.patch(
                        `${process.env.REACT_APP_API_URL}key/${id}/order`,
                        { key_order: normalized },
                        {
                            headers: {
                                "api-key": process.env.REACT_APP_API_KEY,
                                Authorization: `Bearer ${localStorage.getItem("token")}`,
                                "Content-Type": "application/json",
                            },
                        }
                    );
                })
            );
            setOrderDrafts({});
            fetchKeys();
        } catch (err) {
            console.error("Error saving key orders:", err);
            alert("Error saving orders: " + (err.response?.data?.message || err.message || "Unknown error"));
        } finally {
            setIsSavingAllOrders(false);
        }
    };

    const handleDelete = async (keyId) => {
        if (window.confirm("Are you sure you want to delete this key?")) {
            try {
                const res = await axios.delete(`${process.env.REACT_APP_API_URL}key/${keyId}`, {
                    headers: {
                        "api-key": process.env.REACT_APP_API_KEY,
                        Authorization: `Bearer ${localStorage.getItem("token")}`,
                    },
                });
                if (res.data.status === "success") {
                    alert("Key deleted successfully!");
                    fetchKeys();
                } else {
                    alert("Error deleting key: " + res.data.message);
                }
            } catch (err) {
                console.error("Error deleting key:", err);
                alert("Error deleting key: " + (err.response?.data?.message || err.message));
            }
        }
    };

    const handleViewCalculations = async (keyId, keyName) => {
        setCalculationsLoading(true);
        setSelectedKeyName(keyName);
        setShowCalculations(true);
        
        try {
            const res = await axios.get(`${process.env.REACT_APP_API_URL}key/${keyId}/calculations`, {
                headers: {
                    "api-key": process.env.REACT_APP_API_KEY,
                    Authorization: `Bearer ${localStorage.getItem("token")}`,
                },
            });
            
            if (res.data.status === "success") {
                const calculationsData = (res.data.data.calculations || []).map(calc => ({
                    ...calc,
                    is_transferred: Boolean(calc?.is_transferred),
                    transferred_from_key_name: calc?.transferred_from_key_name || calc?.key_name || null,
                    transferred_from_key_id: calc?.transferred_from_key_id || null
                }));
                
                setSelectedKeyCalculations(calculationsData);
                setRecalculatedTransfers(res.data.data.recalculated_transfers || []);
            } else {
                setSelectedKeyCalculations([]);
                setRecalculatedTransfers([]);
                alert("Error fetching calculations: " + res.data.message);
            }
        } catch (err) {
            console.error("Error fetching calculations:", err);
            setSelectedKeyCalculations([]);
            setRecalculatedTransfers([]);
            alert("Error fetching calculations: " + (err.response?.data?.message || err.message));
        } finally {
            setCalculationsLoading(false);
        }
    };

    const closeCalculationsModal = () => {
        setShowCalculations(false);
        setSelectedKeyCalculations([]);
        setSelectedKeyName("");
        setRecalculatedTransfers([]);
    };

    const handleViewResultII = async (keyId, keyName) => {
        setResultIILoading(true);
        setSelectedResultIIKeyName(keyName);
        setShowResultII(true);
        try {
            const res = await axios.get(`${process.env.REACT_APP_API_URL}key/${keyId}/result-ii`, {
                headers: {
                    "api-key": process.env.REACT_APP_API_KEY,
                    Authorization: `Bearer ${localStorage.getItem("token")}`,
                },
            });
            if (res.data.status === "success") {
                setSelectedKeyResultII(res.data.data.result_ii || []);
            } else {
                setSelectedKeyResultII([]);
                alert("Error fetching Result II: " + res.data.message);
            }
        } catch (err) {
            console.error("Error fetching Result II:", err);
            setSelectedKeyResultII([]);
            alert("Error fetching Result II: " + (err.response?.data?.message || err.message));
        } finally {
            setResultIILoading(false);
        }
    };

    const closeResultIIModal = () => {
        setShowResultII(false);
        setSelectedKeyResultII([]);
        setSelectedResultIIKeyName("");
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const formatCosts = (costs) => {
        if (!costs || costs.length === 0) return "No costs";
        
        const formatCostEntry = (cost) => {
            if (!cost) return "";
            const number = cost.cost_number || cost.number || cost.code || cost.id || "";
            const name = cost.cost_name || cost.name || "";
            const value =
                typeof cost === "object"
                    ? cost.cost_value ?? cost.value ?? cost.amount ?? cost.total ?? cost
                    : cost;
            const formattedValue = formatNumberCH(value, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
            });
            const label = [number, name].filter(Boolean).join(" - ");
            return label ? `${label}: ${formattedValue} CHF` : `${formattedValue} CHF`;
        };
        
        return costs.map(formatCostEntry).filter(Boolean).join(", ");
    };

    // Helper function to check if calculation has non-zero results
    // Include calculations with no_cost_destination even if they're 0
    const hasNonZeroCalculation = (calc) => {
        // Include calculations with no_cost_destination even if they're 0
        if (calc.no_cost_destination) {
            return true;
        }
        const withoutVat = parseFloat(calc.result_without_vat || 0);
        const withVat = parseFloat(calc.result_with_vat || 0);
        return withoutVat !== 0 || withVat !== 0;
    };

    const renderDestinationCell = (calc) => {
        const primaryLabel =
            calc?.destination_display ||
            calc?.destination_label ||
            calc?.destination_name ||
            calc?.destination_number ||
            calc?.destination_id ||
            "";
        const secondaryLabel = (() => {
            if (calc?.destination_value === null || calc?.destination_value === undefined) return null;
            const numericValue = parseFloat(calc.destination_value);
            if (Number.isNaN(numericValue)) {
                return calc.destination_value;
            }
            const formatted = formatNumberCH(numericValue);
            return `${formatted}${calc.destination_unit ? ` ${calc.destination_unit}` : ""}`;
        })();

        if (!primaryLabel && !secondaryLabel) {
            return <span className="text-muted">-</span>;
        }

        return (
            <div className="small">
                {primaryLabel && <div><strong>{primaryLabel}</strong></div>}
                {secondaryLabel && <div className="text-muted">{secondaryLabel}</div>}
            </div>
        );
    };

    // Object-routed calc row? Covers BOTH flows:
    //  - Object flow: destination_type === 'object'
    //  - Consumption→Object flow: a 'consumption' calc whose surface_number carries the
    //    Objet code (set by the calc engine; regular consumption keys leave it empty).
    // For both, surface_number holds the Objet code and surface_name holds the real
    // parent surface display string.
    const isObjectCalc = (calc) => {
        const dt = (calc?.destination_type || '').toString().toLowerCase();
        if (dt === 'object') return true;
        const num = (calc?.surface_number || '').toString().trim();
        return dt === 'consumption' && num.length > 0;
    };

    // Object column: the Objet code + its m² (only for object-routed rows).
    const renderObjectCell = (calc) => {
        if (!isObjectCalc(calc)) return <span className="text-muted">-</span>;
        const area = parseFloat(calc.surface_area);
        return (
            <div className="small">
                {calc.surface_number && <div><strong>{calc.surface_number}</strong></div>}
                {area > 0 && (
                    <div className="text-info">
                        {formatNumberCH(area, { minimumFractionDigits: 0, maximumFractionDigits: 2 })} <span className="text-muted">m²</span>
                    </div>
                )}
            </div>
        );
    };

    // Surface column: real parent surface for object rows; surface code+name+m² otherwise.
    const renderSurfaceCell = (calc) => {
        if (isObjectCalc(calc)) {
            return calc.surface_name
                ? <div className="small"><strong>{calc.surface_name}</strong></div>
                : <span className="text-muted">-</span>;
        }
        const area = parseFloat(calc.surface_area);
        if (!(area > 0)) return <span className="text-muted">-</span>;
        return (
            <div className="small">
                {calc.surface_name && calc.surface_number && (
                    <div><strong>{calc.surface_number} - {calc.surface_name}</strong></div>
                )}
                <div className="text-info">
                    {formatNumberCH(area, { minimumFractionDigits: 0, maximumFractionDigits: 2 })} <span className="text-muted">m²</span>
                </div>
            </div>
        );
    };

    const primaryCalculations = selectedKeyCalculations.filter(calc => !calc?.is_transferred && hasNonZeroCalculation(calc));
    const transferredCalculations = selectedKeyCalculations.filter(calc => Boolean(calc?.is_transferred) && hasNonZeroCalculation(calc));
    const transferredSources = Array.from(new Set(
        transferredCalculations
            .map(calc => (calc?.transferred_from_key_name || calc?.key_name || '').toString().trim())
            .filter(name => name.length > 0)
    ));


    return (
        <div className="admin-page">
            <div className="admin-page-title d-flex align-items-center justify-content-between">
                <h1 className="page-section-title">Keys</h1>
                <Link to={`/create-key?returnTo=${returnTo}`} className="btns">
                    <i className="icon-icon-5"></i>
                    Create Key
                </Link>
            </div>

            {/* Filters */}
            <div className="buildings-filter d-flex align-items-center">
                <div className="form-row d-flex">
                    <div className="form-group mb-0">
                        <label>Building</label>
                        <div className="custom-select">
                            <Select
                                options={buildings}
                                value={selectedBuilding}
                                onChange={handleBuildingChange}
                                placeholder="All Buildings"
                                isClearable
                            />
                        </div>
                    </div>
                    <div className="form-group mb-0">
                        <label>Time Period</label>
                        <div className="custom-select">
                            <Select
                                options={periods}
                                value={selectedPeriod}
                                onChange={(selected) => setSelectedPeriod(selected || allPeriodsOption)}
                                placeholder="All Time Periods"
                                isClearable
                                isDisabled={!selectedBuilding || selectedBuilding.value === "All"}
                            />
                        </div>
                    </div>
                </div>
                <div className="form-group mb-0">
                    <label>Search</label>
                    <div className="search-icon">
                        <i className="icon-icon-6"></i>
                        <input
                            className="form-control"
                            placeholder="Search keys"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                </div>
            </div>

            {/* Table */}
            <div className="properties-listing">
                <div className="d-flex align-items-center justify-content-between mb-6">
                    <h3 className="page-section-title mb-0">Keys Listing</h3>
                    {Object.keys(orderDrafts).length > 0 && (
                        <button
                            type="button"
                            className="btns"
                            onClick={handleSaveAllOrders}
                            disabled={isSavingAllOrders}
                        >
                            {isSavingAllOrders ? "Saving..." : `Save Orders (${Object.keys(orderDrafts).length})`}
                        </button>
                    )}
                </div>
                <div className="common-table">
                    <div className="table-wrpper">
                        {loading ? (
                            <div className="text-center py-4">
                                <div className="spinner-border" role="status">
                                    <span className="visually-hidden">Loading...</span>
                                </div>
                                <p className="mt-2">Loading keys...</p>
                            </div>
                        ) : keys.length === 0 ? (
                            <div className="text-center py-4">
                                <p className="text-muted">No keys found.</p>
                                <Link to={`/create-key?returnTo=${returnTo}`} className="btns">
                                    <i className="icon-icon-5"></i>
                                    Create Your First Key
                                </Link>
                            </div>
                        ) : (
                            <table>
                                <thead>
                                    <tr>
                                        <th>Order</th>
                                        <th>Key Name</th>
                                        <th>Building</th>
                                        <th>Formula</th>
                                        <th>Costs</th>
                                        <th>Created</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {keys.map((key) => (
                                        <tr key={key.id}>
                                            <td>
                                                <input
                                                    type="number"
                                                    min="1"
                                                    className="form-control"
                                                    style={{ width: 90 }}
                                                    value={orderDrafts[key.id] ?? (key.key_order ?? "")}
                                                    onChange={(e) => setOrderDrafts((prev) => ({ ...prev, [key.id]: e.target.value }))}
                                                    onKeyDown={(e) => {
                                                        if (e.key === "Enter") {
                                                            e.preventDefault();
                                                            handleSaveAllOrders();
                                                        }
                                                    }}
                                                />
                                            </td>
                                            <td>
                                                <strong>{key.key_name}</strong>
                                                {key.notes && (
                                                    <small className="d-block text-muted">
                                                        {key.notes}
                                                    </small>
                                                )}
                                            </td>
                                            <td>
                                                {key.building_name || 'N/A'}
                                                {key.start_date && key.end_date && (
                                                    <small className="d-block text-muted">
                                                        {new Date(key.start_date).toLocaleDateString()} - {new Date(key.end_date).toLocaleDateString()}
                                                    </small>
                                                )}
                                            </td>
                                            <td>
                                                <code className="small">
                                                    {key.formula || 'No formula'}
                                                </code>
                                            </td>
                                            <td>
                                                <small>{formatCosts(key.costs)}</small>
                                            </td>
                                            <td>
                                                <small>{formatDate(key.created_at)}</small>
                                            </td>
                                            <td>
                                                <div className="d-flex button-group">
                                                    <button
                                                        className="btns-table view-btn"
                                                        onClick={() => handleViewCalculations(key.id, key.key_name)}
                                                        title="View Calculations"
                                                        style={{ backgroundColor: '#28a745', marginRight: '5px', color: '#fff' }}
                                                    >
                                                        <i className="icon-icon-6"></i>
                                                        Calculations
                                                    </button>
                                                    <button
                                                        className="btns-table view-btn"
                                                        onClick={() => handleViewResultII(key.id, key.key_name)}
                                                        title="View Result II"
                                                        style={{ backgroundColor: '#17a2b8', marginRight: '5px', color: '#fff' }}
                                                    >
                                                        <i className="icon-icon-6"></i>
                                                        Result II
                                                    </button>
                                                    <Link
                                                        to={`/reports/${key.id}`}
                                                        className="btns-table view-btn"
                                                        title="View Report"
                                                        style={{ backgroundColor: '#6f42c1', marginRight: '5px', color: '#fff' }}
                                                    >
                                                        <i className="icon-icon-6"></i>
                                                        View Report
                                                    </Link>
                                                    <Link
                                                        to={`/create-key?edit=${key.id}&returnTo=${returnTo}`}
                                                        className="btns-table edit-btn"
                                                        title="Edit Key"
                                                    >
                                                        <i className="icon-icon-8"></i>
                                                        Edit
                                                    </Link>
                                                    <Link 
                                                        to={`/create-key?duplicate=${key.id}&returnTo=${returnTo}`}
                                                        className="btns-table duplicate-btn"
                                                        title="Duplicate Key"
                                                    >
                                                        <i className="icon-icon-8"></i>
                                                        Duplicate
                                                    </Link>
                                                    <button
                                                        className="btns-table delete-btn"
                                                        onClick={() => handleDelete(key.id)}
                                                        title="Delete Key"
                                                    >
                                                        <i className="icon-icon-14"></i>
                                                        Delete
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </div>
                </div>

                {/* Pagination */}
                {!loading && keys.length > 0 && (
                    <div className="table-pagination d-flex align-items-center justify-content-between gap-2 mt-4">
                        <div className="pagination-info">
                            Showing {(page - 1) * pageSize + 1} -{" "}
                            {Math.min(page * pageSize, pagination.total)} of {pagination.total}
                        </div>
                        <div className="pagination-controls d-flex align-items-center">
                            <button
                                className="btns-table prev-btn"
                                onClick={() => setPage((p) => Math.max(p - 1, 1))}
                                disabled={page === 1}
                            >
                                <i className="icon-icon-10"></i>
                            </button>
                            <button
                                className="btns-table next-btn"
                                onClick={() => setPage((p) => Math.min(p + 1, pagination.totalPages))}
                                disabled={page === pagination.totalPages}
                            >
                                <i className="icon-icon-9"></i>
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {/* Calculations Modal */}
            {showCalculations && (
                <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog modal-xl">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">
                                    Calculations for "{selectedKeyName}"
                                </h5>
                                <button
                                    type="button"
                                    className="btn-close"
                                    onClick={closeCalculationsModal}
                                ></button>
                            </div>
                            <div className="modal-body">
                                {calculationsLoading ? (
                                    <div className="text-center py-4">
                                        <div className="spinner-border" role="status">
                                            <span className="visually-hidden">Loading...</span>
                                        </div>
                                        <p className="mt-2">Loading calculations...</p>
                                    </div>
                                ) : selectedKeyCalculations.length === 0 ? (
                                    <div className="text-center py-4">
                                        <p className="text-muted">No calculations found for this key.</p>
                                        <small>Calculations are generated automatically when a key is created with a formula, costs, and destinations.</small>
                                    </div>
                                ) : (
                                    <>
                                        <div className="table-responsive">
                                            <table className="table table-striped">
                                                <thead>
                                                    <tr>
                                                        <th>Type</th>
                                                        <th>Origin</th>
                                                        <th>Cost</th>
                                                        <th>Object</th>
                                                        <th>Surface</th>
                                                        <th>Consumption</th>
                                                        <th>Variables</th>
                                                        <th>Destination Type</th>
                                                        <th>Destination</th>
                                                        <th>Result Without VAT</th>
                                                        <th>Result With VAT</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {primaryCalculations.length === 0 ? (
                                                        <tr>
                                                            <td colSpan="11" className="text-center text-muted py-4">
                                                                No primary calculations found.
                                                            </td>
                                                        </tr>
                                                    ) : (
                                                        primaryCalculations.map((calc, index) => (
                                                            <tr key={calc.id || index}>
                                                                <td>
                                                                    <span className="badge bg-primary">
                                                                        {calc.calculation_type}
                                                                    </span>
                                                                </td>
                                                                <td>
                                                                    <span className="badge bg-success">Primary</span>
                                                                </td>
                                                                <td>
                                                                    {calc.no_cost_destination ? (
                                                                        <span className="text-muted">-</span>
                                                                    ) : (
                                                                        <div className="small">
                                                                            {calc.cost_name && calc.cost_number && (
                                                                                <div><strong>{calc.cost_number} - {calc.cost_name}</strong></div>
                                                                            )}
                                                                            <div className="text-success">
                                                                                {formatNumberCH(calc.cost_value || calc.cost || 0, {
                                                                                    minimumFractionDigits: 2,
                                                                                    maximumFractionDigits: 2
                                                                                })} <span className="text-muted">CHF</span>
                                                                            </div>
                                                                        </div>
                                                                    )}
                                                                </td>
                                                                <td>{renderObjectCell(calc)}</td>
                                                                <td>{renderSurfaceCell(calc)}</td>
                                                                <td>
                                                                    <div className="small">
                                                                        {calc.consumption > 0 ? (
                                                                            <div>
                                                                                {calc.consumption_name && calc.consumption_number && (
                                                                                    <div><strong>{calc.consumption_number} - {calc.consumption_name}</strong></div>
                                                                                )}
                                                                                <div className="text-warning">
                                                                                    {formatNumberCH(calc.consumption, {
                                                                                        minimumFractionDigits: 0,
                                                                                        maximumFractionDigits: 2
                                                                                    })} <span className="text-muted">kWh</span>
                                                                                </div>
                                                                            </div>
                                                                        ) : (
                                                                            <span className="text-muted">-</span>
                                                                        )}
                                                                    </div>
                                                                </td>
                                                                <td>
                                                                    <div className="small">
                                                                        {Object.entries(calc.variables || {}).map(([key, value]) => {
                                                                            if (key === 'surface_area' || key === 'consumption' || key === 'cost') {
                                                                                return null;
                                                                            }
                                                                            let displayValue = typeof value === 'number' ? formatNumberCH(value) : value;
                                                                            let unit = '';
                                                                            if (key === 'total_surface') {
                                                                                unit = ' m²';
                                                                            } else if (key === 'total_consumption') {
                                                                                unit = ' kWh';
                                                                            } else if (key === 'ponderation' || key === 'total_key_ponderation') {
                                                                                unit = '';
                                                                            }
                                                                            return (
                                                                                <div key={key}>
                                                                                    <strong>{key}:</strong> {displayValue}<span className="text-muted">{unit}</span>
                                                                                </div>
                                                                            );
                                                                        })}
                                                                    </div>
                                                                </td>
                                                                <td>
                                                                    {calc.destination_type ? (
                                                                        <span className="badge bg-info text-dark text-uppercase">
                                                                            {calc.destination_type}
                                                                        </span>
                                                                    ) : (
                                                                        <span className="text-muted">-</span>
                                                                    )}
                                                                </td>
                                                                <td>{renderDestinationCell(calc)}</td>
                                                                <td>
                                                                    <strong className="text-success">
                                                                        {calc.result_without_vat !== null && calc.result_without_vat !== undefined ? 
                                                                            formatNumberCH(parseFloat(calc.result_without_vat) || 0, {
                                                                                minimumFractionDigits: 2,
                                                                                maximumFractionDigits: 2
                                                                            }) : '-'}
                                                                        {calc.result_unit && calc.result_without_vat && <span className="text-muted ms-1">{calc.result_unit}</span>}
                                                                    </strong>
                                                                </td>
                                                                <td>
                                                                    <strong className="text-primary">
                                                                        {calc.result_with_vat !== null && calc.result_with_vat !== undefined ? 
                                                                            formatNumberCH(parseFloat(calc.result_with_vat) || 0, {
                                                                                minimumFractionDigits: 2,
                                                                                maximumFractionDigits: 2
                                                                            }) : '-'}
                                                                        {calc.result_unit && calc.result_with_vat && <span className="text-muted ms-1">{calc.result_unit}</span>}
                                                                    </strong>
                                                                </td>
                                                            </tr>
                                                        ))
                                                    )}
                                                </tbody>
                                            </table>
                                        </div>

                                        <div className="mt-4 p-3 bg-light rounded">
                                            <h6>Summary</h6>
                                            <div className="row">
                                                <div className="col-md-3">
                                                    <strong>Total Calculations:</strong> {primaryCalculations.length}
                                                </div>
                                                <div className="col-md-3">
                                                    <strong>Total Without VAT:</strong> {formatNumberCH(primaryCalculations.reduce((sum, calc) => sum + parseFloat(calc.result_without_vat || 0), 0), {
                                                        minimumFractionDigits: 2,
                                                        maximumFractionDigits: 2
                                                    })} CHF
                                                </div>
                                                <div className="col-md-3">
                                                    <strong>Total With VAT:</strong> {formatNumberCH(primaryCalculations.reduce((sum, calc) => sum + parseFloat(calc.result_with_vat || 0), 0), {
                                                        minimumFractionDigits: 2,
                                                        maximumFractionDigits: 2
                                                    })} CHF
                                                </div>
                                                <div className="col-md-3">
                                                    <strong>VAT Impact:</strong> {(() => {
                                                        const withVatTotal = primaryCalculations.reduce((sum, calc) => sum + parseFloat(calc.result_with_vat || 0), 0);
                                                        const withoutVatTotal = primaryCalculations.reduce((sum, calc) => sum + parseFloat(calc.result_without_vat || 0), 0);
                                                        const impact = withVatTotal - withoutVatTotal;
                                                        return formatNumberCH(impact === 0 ? 0 : impact, {
                                                            minimumFractionDigits: 2,
                                                            maximumFractionDigits: 2
                                                        });
                                                    })()} CHF
                                                </div>
                                                {transferredCalculations.length > 0 && (
                                                    <div className="col-md-3 mt-3">
                                                        <strong>Transferred Calculations:</strong> {transferredCalculations.length}
                                                    </div>
                                                )}
                                            </div>
                                        </div>

                                        {transferredCalculations.length > 0 && (
                                            <div className="mt-5">
                                                <h6>Transferred Calculations</h6>
                                                {transferredSources.length > 0 && (
                                                    <p className="text-muted small mb-3">
                                                        Source keys: {transferredSources.join(', ')}
                                                    </p>
                                                )}
                                                <div className="table-responsive">
                                                    <table className="table table-striped">
                                                        <thead>
                                                            <tr>
                                                                <th>Type</th>
                                                                <th>Origin</th>
                                                                <th>Cost</th>
                                                                <th>Object</th>
                                                                <th>Surface</th>
                                                                <th>Consumption</th>
                                                                <th>Variables</th>
                                                                <th>Destination Type</th>
                                                                <th>Destination</th>
                                                                <th>Result Without VAT</th>
                                                                <th>Result With VAT</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {transferredCalculations.map((calc, index) => (
                                                                <tr key={`transferred-${calc.id || index}`}>
                                                                    <td>
                                                                        <span className="badge bg-primary">
                                                                            {calc.calculation_type}
                                                                        </span>
                                                                    </td>
                                                                    <td>
                                                                        <span className="badge bg-warning text-dark">Transferred</span>
                                                                        <div className="small text-muted mt-1">
                                                                            Source: {calc.transferred_from_key_name || calc.key_name || 'Unknown'}
                                                                        </div>
                                                                    </td>
                                                                    <td>
                                                                        {calc.no_cost_destination ? (
                                                                            <span className="text-muted">-</span>
                                                                        ) : (
                                                                            <div className="small">
                                                                                {calc.cost_name && calc.cost_number && (
                                                                                    <div><strong>{calc.cost_number} - {calc.cost_name}</strong></div>
                                                                                )}
                                                                                <div className="text-success">
                                                                                    {formatNumberCH(calc.cost_value || calc.cost || 0, {
                                                                                        minimumFractionDigits: 2,
                                                                                        maximumFractionDigits: 2
                                                                                    })} <span className="text-muted">CHF</span>
                                                                                </div>
                                                                            </div>
                                                                        )}
                                                                    </td>
                                                                    <td>{renderObjectCell(calc)}</td>
                                                                    <td>{renderSurfaceCell(calc)}</td>
                                                                    <td>
                                                                        <div className="small">
                                                                            {calc.consumption > 0 ? (
                                                                                <div>
                                                                                    {calc.consumption_name && calc.consumption_number && (
                                                                                        <div><strong>{calc.consumption_number} - {calc.consumption_name}</strong></div>
                                                                                    )}
                                                                                    <div className="text-warning">
                                                                                        {formatNumberCH(calc.consumption, {
                                                                                            minimumFractionDigits: 0,
                                                                                            maximumFractionDigits: 2
                                                                                        })} <span className="text-muted">kWh</span>
                                                                                    </div>
                                                                                </div>
                                                                            ) : (
                                                                                <span className="text-muted">-</span>
                                                                            )}
                                                                        </div>
                                                                    </td>
                                                                    <td>
                                                                        <div className="small">
                                                                            {Object.entries(calc.variables || {}).map(([key, value]) => {
                                                                                if (key === 'surface_area' || key === 'consumption' || key === 'cost') {
                                                                                    return null;
                                                                                }
                                                                                // Use raw totals for display if available, otherwise use original values
                                                                                let displayValue = typeof value === 'number' ? formatNumberCH(value) : value;
                                                                                let unit = '';
                                                                                let displayKey = key;
                                                                                
                                                                                // Replace total_surface and total_consumption with raw values for display
                                                                                if (key === 'total_surface' && calc.variables?.raw_total_surface !== undefined) {
                                                                                    displayValue = typeof calc.variables.raw_total_surface === 'number' 
                                                                                        ? formatNumberCH(calc.variables.raw_total_surface) 
                                                                                        : calc.variables.raw_total_surface;
                                                                                    unit = ' m²';
                                                                                } else if (key === 'total_consumption' && calc.variables?.raw_total_consumption !== undefined) {
                                                                                    displayValue = typeof calc.variables.raw_total_consumption === 'number' 
                                                                                        ? formatNumberCH(calc.variables.raw_total_consumption) 
                                                                                        : calc.variables.raw_total_consumption;
                                                                                    unit = ' kWh';
                                                                                } else if (key === 'total_surface') {
                                                                                    unit = ' m²';
                                                                                } else if (key === 'total_consumption') {
                                                                                    unit = ' kWh';
                                                                                } else if (key === 'ponderation' || key === 'total_key_ponderation') {
                                                                                    unit = '';
                                                                                } else if (key === 'raw_total_surface' || key === 'raw_total_consumption') {
                                                                                    // Skip raw totals as they're already shown via total_surface/total_consumption
                                                                                    return null;
                                                                                }
                                                                                return (
                                                                                    <div key={key}>
                                                                                        <strong>{displayKey}:</strong> {displayValue}<span className="text-muted">{unit}</span>
                                                                                    </div>
                                                                                );
                                                                            })}
                                                                        </div>
                                                                    </td>
                                                                    <td>
                                                                        {calc.destination_type ? (
                                                                            <span className="badge bg-info text-dark text-uppercase">
                                                                                {calc.destination_type}
                                                                            </span>
                                                                        ) : (
                                                                            <span className="text-muted">-</span>
                                                                        )}
                                                                    </td>
                                                                    <td>{renderDestinationCell(calc)}</td>
                                                                    <td>
                                                                        <strong className="text-success">
                                                                            {calc.result_without_vat !== null && calc.result_without_vat !== undefined ? 
                                                                                formatNumberCH(parseFloat(calc.result_without_vat) || 0, {
                                                                                    minimumFractionDigits: 2,
                                                                                    maximumFractionDigits: 2
                                                                                }) : '-'}
                                                                            {calc.result_unit && calc.result_without_vat && <span className="text-muted ms-1">{calc.result_unit}</span>}
                                                                        </strong>
                                                                    </td>
                                                                    <td>
                                                                        <strong className="text-primary">
                                                                            {calc.result_with_vat !== null && calc.result_with_vat !== undefined ? 
                                                                                formatNumberCH(parseFloat(calc.result_with_vat) || 0, {
                                                                                    minimumFractionDigits: 2,
                                                                                    maximumFractionDigits: 2
                                                                                }) : '-'}
                                                                            {calc.result_unit && calc.result_with_vat && <span className="text-muted ms-1">{calc.result_unit}</span>}
                                                                        </strong>
                                                                    </td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            </div>
                                        )}

                                        {recalculatedTransfers.length > 0 && (
                                            <div className="mt-5">
                                                <h6>Recalculated Transfer Allocations</h6>
                                                {recalculatedTransfers.map((section, sectionIndex) => (
                                                    <div className="mt-4" key={`recalc-section-${section.source_key_id || sectionIndex}`}>
                                                        <div className="fw-semibold mb-2">
                                                            Source Key: {section.source_key_name || `Key ${section.source_key_id || ''}`}
                                                        </div>
                                                        {section.calculations && section.calculations.length > 0 ? (
                                                            <div className="table-responsive">
                                                                <table className="table table-striped">
                                                                    <thead>
                                                                <tr>
                                                                    <th>Type</th>
                                                                    <th>Origin</th>
                                                                    <th>Cost</th>
                                                                    <th>Object</th>
                                                                    <th>Surface</th>
                                                                    <th>Consumption</th>
                                                                    <th>Variables</th>
                                                                    <th>Destination Type</th>
                                                                    <th>Destination</th>
                                                                    <th>Result Without VAT</th>
                                                                    <th>Result With VAT</th>
                                                                </tr>
                                                                    </thead>
                                                                    <tbody>
                                                                        {section.calculations.map((calc, index) => (
                                                                            <tr key={`recalc-${section.source_key_id || sectionIndex}-${index}`}>
                                                                                <td>
                                                                                    <span className="badge bg-info text-dark">
                                                                                        {calc.calculation_type}
                                                                                    </span>
                                                                                </td>
                                                                                <td>
                                                                                    <span className="badge bg-secondary">Recalculated</span>
                                                                                    <div className="small text-muted mt-1">
                                                                                        Source: {calc.transferred_from_key_name || section.source_key_name || 'Unknown'}
                                                                                    </div>
                                                                                </td>
                                                                                <td>
                                                                                    {calc.no_cost_destination ? (
                                                                                        <span className="text-muted">-</span>
                                                                                    ) : (
                                                                                        <div className="small">
                                                                                            {(calc.cost_number || calc.cost_name) && (
                                                                                                <div>
                                                                                                    <strong>
                                                                                                        {[calc.cost_number, calc.cost_name].filter(Boolean).join(" - ")}
                                                                                                    </strong>
                                                                                                </div>
                                                                                            )}
                                                                                            <div className="text-success">
                                                                                                {formatNumberCH(parseFloat(calc.cost_value) || 0, {
                                                                                                    minimumFractionDigits: 2,
                                                                                                    maximumFractionDigits: 2
                                                                                                })} <span className="text-muted">CHF</span>
                                                                                            </div>
                                                                                        </div>
                                                                                    )}
                                                                                </td>
                                                                                <td>{renderObjectCell(calc)}</td>
                                                                                <td>{renderSurfaceCell(calc)}</td>
                                                                                <td>
                                                                                    <div className="small">
                                                                                        {calc.consumption > 0 ? (
                                                                                            <div>
                                                                                                {(calc.consumption_number || calc.consumption_name) && (
                                                                                                    <div>
                                                                                                        <strong>
                                                                                                            {[calc.consumption_number, calc.consumption_name].filter(Boolean).join(" - ")}
                                                                                                        </strong>
                                                                                                    </div>
                                                                                                )}
                                                                                                <div className="text-warning">
                                                                                                    {formatNumberCH(calc.consumption, {
                                                                                                        minimumFractionDigits: 0,
                                                                                                        maximumFractionDigits: 2
                                                                                                    })} <span className="text-muted">kWh</span>
                                                                                                </div>
                                                                                            </div>
                                                                                        ) : (
                                                                                            <span className="text-muted">-</span>
                                                                                        )}
                                                                                    </div>
                                                                                </td>
                                                                                <td>
                                                                                    <div className="small">
                                                                                        {Object.entries(calc.variables || {}).map(([key, value]) => {
                                                                                            if (key === 'surface_area' || key === 'consumption' || key === 'cost') {
                                                                                                return null;
                                                                                            }
                                                                                            let displayValue = typeof value === 'number' ? formatNumberCH(value) : value;
                                                                                            let unit = '';
                                                                                            if (key === 'total_surface') {
                                                                                                unit = ' m²';
                                                                                            } else if (key === 'total_consumption') {
                                                                                                unit = ' kWh';
                                                                                            }
                                                                                            return (
                                                                                                <div key={key}>
                                                                                                    <strong>{key}:</strong> {displayValue}<span className="text-muted">{unit}</span>
                                                                                                </div>
                                                                                            );
                                                                                        })}
                                                                                    </div>
                                                                                </td>
                                                                                <td>
                                                                                    {calc.destination_type ? (
                                                                                        <span className="badge bg-info text-dark text-uppercase">
                                                                                            {calc.destination_type}
                                                                                        </span>
                                                                                    ) : (
                                                                                        <span className="text-muted">-</span>
                                                                                    )}
                                                                                </td>
                                                                                <td>{renderDestinationCell(calc)}</td>
                                                                                <td>
                                                                                    <strong className="text-success">
                                                                                        {calc.result_without_vat !== null && calc.result_without_vat !== undefined ?
                                                                                            formatNumberCH(parseFloat(calc.result_without_vat) || 0, {
                                                                                                minimumFractionDigits: 2,
                                                                                                maximumFractionDigits: 2
                                                                                            }) : '-'}
                                                                                        {calc.result_unit && calc.result_without_vat && <span className="text-muted ms-1">{calc.result_unit}</span>}
                                                                                    </strong>
                                                                                </td>
                                                                                <td>
                                                                                    <strong className="text-primary">
                                                                                        {calc.result_with_vat !== null && calc.result_with_vat !== undefined ?
                                                                                            formatNumberCH(parseFloat(calc.result_with_vat) || 0, {
                                                                                                minimumFractionDigits: 2,
                                                                                                maximumFractionDigits: 2
                                                                                            }) : '-'}
                                                                                        {calc.result_unit && calc.result_with_vat && <span className="text-muted ms-1">{calc.result_unit}</span>}
                                                                                    </strong>
                                                                                </td>
                                                                            </tr>
                                                                        ))}
                                                                    </tbody>
                                                                </table>
                                                            </div>
                                                        ) : (
                                                            <p className="text-muted small">No recalculated results for this source key.</p>
                                                        )}
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>
                            <div className="modal-footer">
                                <button
                                    type="button"
                                    className="btn btn-secondary"
                                    onClick={closeCalculationsModal}
                                >
                                    Close
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Result II Modal */}
            {showResultII && (
                <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog modal-xl">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">
                                    Result II for "{selectedResultIIKeyName}"
                                </h5>
                                <button
                                    type="button"
                                    className="btn-close"
                                    onClick={closeResultIIModal}
                                ></button>
                            </div>
                            <div className="modal-body">
                                {resultIILoading ? (
                                    <div className="text-center py-4">
                                        <div className="spinner-border" role="status">
                                            <span className="visually-hidden">Loading...</span>
                                        </div>
                                        <p className="mt-2">Loading Result II...</p>
                                    </div>
                                ) : selectedKeyResultII.length === 0 ? (
                                    <div className="text-center py-4">
                                        <p className="text-muted">No Result II rows found for this key.</p>
                                        <small>Result II rows are produced when the key has destinations and matching objects in the period.</small>
                                    </div>
                                ) : (
                                    <>
                                        <div className="d-flex justify-content-between align-items-center mb-2">
                                            <small className="text-muted">{selectedKeyResultII.length} rows</small>
                                            <small className="text-muted">
                                                Sum:&nbsp;
                                                <strong>
                                                    {formatNumberCH(
                                                        selectedKeyResultII.reduce((s, r) => s + (Number(r.result_without_vat) || 0), 0),
                                                        { minimumFractionDigits: 2, maximumFractionDigits: 2 }
                                                    )} CHF
                                                </strong>
                                                &nbsp;without VAT &nbsp;|&nbsp;
                                                <strong>
                                                    {formatNumberCH(
                                                        selectedKeyResultII.reduce((s, r) => s + (Number(r.result_with_vat) || 0), 0),
                                                        { minimumFractionDigits: 2, maximumFractionDigits: 2 }
                                                    )} CHF
                                                </strong>
                                                &nbsp;with VAT
                                            </small>
                                        </div>
                                        <div className="table-responsive" style={{ maxHeight: '60vh' }}>
                                            <table className="table table-striped table-sm" style={{ fontSize: '13px' }}>
                                                <thead style={{ position: 'sticky', top: 0, background: '#fff', zIndex: 1 }}>
                                                    <tr>
                                                        <th>Cost</th>
                                                        <th>Object</th>
                                                        <th>Surface</th>
                                                        <th>Consumption</th>
                                                        <th>Destination</th>
                                                        <th className="text-end">Without VAT</th>
                                                        <th className="text-end">With VAT</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {selectedKeyResultII.map((row, idx) => (
                                                        <tr key={idx}>
                                                            <td>{row.cost_number || '-'}</td>
                                                            <td>{row.object || '-'}</td>
                                                            <td>{row.surface || '-'}</td>
                                                            <td>{row.consumption || '-'}</td>
                                                            <td>{row.destination || '-'}</td>
                                                            <td className="text-end">
                                                                {formatNumberCH(row.result_without_vat, { minimumFractionDigits: 2, maximumFractionDigits: 4 })}
                                                            </td>
                                                            <td className="text-end">
                                                                {formatNumberCH(row.result_with_vat, { minimumFractionDigits: 2, maximumFractionDigits: 4 })}
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </>
                                )}
                            </div>
                            <div className="modal-footer">
                                <button
                                    type="button"
                                    className="btn btn-secondary"
                                    onClick={closeResultIIModal}
                                >
                                    Close
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Keys;
